
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Switch, Button, Box, Typography } from '@mui/material';
import { alertActions, configAction } from '_store';
import { AutocompleteInput, DropdownTableInput } from '_components';
import Grid from '@mui/material/Grid2';
import _ from 'lodash';
import Checkbox from '@mui/material/Checkbox';
import { useCheckAdminRole } from '_utils/role-access';
import { ERR_MSG } from 'constants/labels';

const PortalConfiguration = ({ control, errors, data, options, setData, initialData, selectedPortal, handlePortalChange, portalName, handleFetch }) => {
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
    const authUser = useSelector(x => x.auth?.value);
    const userProfiles = useSelector(x => x.userProfile?.userProfileData);
    const hasUpdateUserRolesAccess = useCheckAdminRole("UpdateUserRoles")
    const [isDirty, setIsDirty] = useState(false);
    const user = authUser?.Data?.UserDetails;
    const name = `${user.firstName} ${user.lastName}`;

    const pivotData = useCallback((portal) => {
        const result = [];
        const accessNames = new Set();
        const roles = {};

        portal?.PortalRoleAccess?.forEach(roleAccess => {
            roleAccess?.FeatureAccess.forEach(permission => {
                accessNames.add(permission.AccessID);
                if (!roles[permission.AccessID]) {
                    roles[permission.AccessID] = {};
                }
                roles[permission.AccessID][roleAccess.RoleID] = permission.IsActive;
            });
        });

        accessNames.forEach(featureId => {
            const featureName = portal.PortalRoleAccess[0].FeatureAccess?.find(permission => permission.AccessID === featureId)?.AccessName;
            const row = { AccessID: featureId, FeatureName: featureName };
            Object.keys(roles[featureId]).forEach(roleId => {
                row[roleId] = roles[featureId][roleId];
            });
            result.push(row);
        });

        return result;
    }, []);

    const pivotedData = useMemo(() => {
        return pivotData(data);
    }, [data, pivotData]);



    const handleToggle = (featureId, roleId) => {
        setIsDirty(true);
        setData(prevData => {
            const updatedData = _.cloneDeep(prevData);
            updatedData[0]?.PortalRoleAccess?.forEach(roleAccess => {
                roleAccess?.FeatureAccess.forEach(permission => {
                    if (permission.AccessID === parseInt(featureId) && roleAccess.RoleID === parseInt(roleId)) {
                        permission.IsActive = !permission.IsActive;
                    }
                });
            });
            console.log('Updated Data:', updatedData); // Debugging statement
            return updatedData;
        });
    };


    const handleSubmit = async () => {
        const changedData = [];

        data?.PortalRoleAccess.forEach(roleAccess => {
            const initialRoleAccess = initialData?.PortalRoleAccess?.find(r => r.RoleID === roleAccess.RoleID);

            roleAccess.FeatureAccess.forEach(permission => {
                const initialPermission = initialRoleAccess?.FeatureAccess.find(p => p.AccessID === permission.AccessID);

                if (initialPermission && permission.IsActive !== initialPermission.IsActive) {
                    changedData.push({
                        RoleAccessMappingID: permission.RoleAccessMappingID,
                        IsActive: permission.IsActive,
                        CreatedBy: name
                    });
                }
            });
        });


        try {
            dispatch(alertActions.clear());
            const result = await dispatch(configAction.postAccess(changedData)).unwrap();
            if(result.Succeeded) {
                dispatch(alertActions.success({ message: result?.Message, showAfterRedirect: true, header: "Role Management" }));
                handleFetch();
                setIsDirty(false);
            }
        } catch (error) {
            dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header: "Role Management" }));
        }
    };

    const columns = useMemo(() => {
        const dynamicColumns = [
            {
                header: 'Feature Name', pin: 'left', accessorKey: 'FeatureName', muiTableBodyCellProps: {
                    enableColumnFilter: true,
                    enableColumnSorting: true,
                    sx: {
                        textAlign: 'left',
                        justifyContent: 'flex-start',
                        paddingLeft: "5px"
                    },
                },
            }
        ];
        if (pivotedData.length > 0) {
            Object.keys(pivotedData[0]).forEach(key => {
                if (key !== 'AccessID' && key !== 'FeatureName') {
                    const role = data.PortalRoleAccess?.find(roleAccess => roleAccess.RoleID === parseInt(key)).RoleName;
                    dynamicColumns.push({
                        header: role,
                        accessorKey: key,
                        enableSorting: false,
                        enableColumnFilter: false,
                        textAlign: "left",
                        Cell: ({ cell }) => (
                            <Checkbox
                                checked={cell.getValue() === true}
                                color="primary"
                                disabled={!hasUpdateUserRolesAccess}
                                onChange={() => handleToggle(cell.row.original.AccessID, key)}
                            />
                        ),
                    });
                }
            });
        }
        return dynamicColumns;
    }, [pivotedData, data]);

    const table = useMaterialReactTable({
        columns,
        data: pivotedData,
        state: { pagination },
        onPaginationChange: setPagination,
        autoResetPageIndex: false, // Prevents resetting to the first page
        enableSorting: true,
        enablePagination: true,
        enableHiding: false,
        enableGlobalFilter: false,
        enableStickyHeader: true,
        enableColumnFilter: true,
        enableFullScreenToggle: false,
        columnFilterDisplayMode: 'popover',
        enableColumnActions: false,
        initialState: {
            pagination: { pageSize: 5, pageIndex: 0 }, // Set initial rows per page
        },
        muiTableHeadCellProps: {
            sx: {
                textAlign: "center",
            },
        },
        muiTableBodyCellProps: {
            sx: {
                textAlign: "center !important",
                "& svg": {
                    fill: "#0059B3 !important"
                }
            },
        },
        renderEmptyRowsFallback: ({ table }) => (
            <Typography sx={{ padding: 3, textAlign: "center", fontStyle: "italic" }}>No results found for the provided search criteria</Typography>
        ),
        initialState: {
            columnPinning: { left: ['FeatureName'] },
        },
        renderTopToolbarCustomActions: () => (

            <Grid container className="portalconfigurationwidth" >
                <Grid size={{ xs: 12, sm: 12, md: 12 }} className='passwordcheck'>
                    {/* <AutocompleteInput
                            control={control}
                            name="selectedPortal"
                            label="Select Portal"
                            value={options?.find(option => option.value === selectedPortal || null)}
                            options={options}
                            error={!!errors.selectedPortal}
                            helperText={errors.selectedPortal?.message}
                            
                            onChange={handlePortalChange}
                        /> */}
                    <DropdownTableInput

                        name="selectedPortal"
                        label="Select Portal"
                        value={selectedPortal}
                        options={options}
                        error={!!errors.selectedPortal}
                        //  helperText={errors.selectedPortal?.message}
                        onChange={(value) => handlePortalChange(value)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12 }}  >

                    {/* <Typography variant="h2" className='portalconfiguration'>{portalName}</Typography> */}
                </Grid>
            </Grid>
        ),
    });

    return (
        <div>

            <MaterialReactTable table={table} />
            <Button className='submitbutton' variant="contained" color="primary" onClick={handleSubmit} disabled={!isDirty}>
                Submit
            </Button>
        </div>
    );
};

export default PortalConfiguration;
